/**
 * pasteSanitizer.ts — pure helpers for cleaning clipboard content pasted into
 * the chat composer (the PromptInput contentEditable, see styles.ts).
 *
 * Owner report (Miles 2026-07-13):
 *   1. Pasted text sometimes arrives with near-white inline colors that are
 *      unreadable on the light composer. Rule: keep the user's colors
 *      IF AND ONLY IF they have adequate contrast; otherwise fall back to the
 *      scheme's near-black text color.
 *   2. Pastes drag along empty wrapper blocks / <br> chains that render as
 *      un-removable blank space (PromptInput is `white-space: pre-wrap`, so
 *      even bare newline text nodes between blocks render as blank lines).
 *   3. Otherwise the paste must stay formatted the way the user pasted it
 *      (bold/italic/lists/links/line structure preserved).
 *
 * SAFETY is delegated to the composer's existing allowlist sanitizer
 * (`sanitizeHtmlInput` in ./utils): it already strips disallowed tags,
 * event-handler attributes (only style/class survive on non-anchors) and
 * non-http(s) hrefs (so javascript: URLs unwrap). This module only adds the
 * paste-specific passes on top — it deliberately does NOT invent a parallel
 * allowlist. Script/style/iframe/object CONTENT is dropped entirely here
 * first, because `sanitizeHtmlInput` UNWRAPS disallowed tags (which would
 * leak script/style text into the message).
 */

import { sanitizeHtmlInput } from './utils';

// ─── Scheme constants ────────────────────────────────────────────────────────

/** Composer background — `PromptInput` in styles.ts (`background: #FAFAF8`). */
export const COMPOSER_BACKGROUND_COLOR = '#FAFAF8';

/**
 * Scheme text color pasted-in unreadable colors are replaced with.
 * Matches the composer's own `caret-color: #0f172a` (PromptInput, styles.ts)
 * — the near-black slate the composer text renders in.
 */
export const COMPOSER_TEXT_COLOR = '#0f172a';

/**
 * Minimum WCAG contrast ratio an inline `color` must reach against the
 * composer background (#FAFAF8) to be KEPT. Below this the color is replaced
 * with COMPOSER_TEXT_COLOR. 2.5:1 is deliberately below the AA reading
 * threshold (4.5:1): the owner's rule is "replace if and only if too white",
 * so we only intervene on genuinely illegible colors (grays lighter than
 * ~rgb(160,160,160) trip this; mid-gray #808080, red, blue etc. all pass).
 */
export const MIN_PASTE_CONTRAST_RATIO = 2.5;

/** Pasted font-size cap, in em. Sizes above this are capped (not stripped). */
export const MAX_PASTE_FONT_SIZE_EM = 2;

/**
 * Inline style properties that describe a BOX rather than TEXT, dropped from
 * every pasted element (owner report 2026-08-26).
 *
 * The report: a message copied out of the transcript and pasted back rendered
 * as a bubble hanging off the right edge of the screen, carrying the original's
 * colours and indentation. Two things produced that, and this list is half the
 * answer — `sanitizeHtmlInput`'s ALLOWED_STYLES admits `width`, `min-width`,
 * `padding`, `margin`, `display`, `white-space` and friends, so a Chrome-
 * annotated `width: 812px` or a `white-space: nowrap` rode straight into the
 * composer and then into the stored message. (The other half is the `class`
 * attribute — see stripPresentationClasses.)
 *
 * The owner drew the line precisely: "there should be some formatting like
 * font, size, italics, underline, strikethrough, and much more but it should
 * not copy the color scheme, indentation and all that." So this drops geometry,
 * spacing and paint; it keeps everything that describes the TEXT — weight,
 * style, decoration, size, family, letter-spacing, line-height, alignment.
 *
 * `text-align` is deliberately KEPT: it is a property of the writing, not of
 * the box, and a pasted centred heading should stay centred.
 */
export const DROPPED_PASTE_STYLE_PROPERTIES: readonly string[] = [
  // Geometry — the direct cause of content hanging off the screen.
  'width', 'min-width', 'max-width', 'height', 'min-height', 'max-height',
  // Spacing — the "indentation" the owner called out. List indents come back
  // from the composer's own `ul, ol` rules, so nothing is lost.
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  // Layout — a pasted `display: flex` re-lays-out the composer's own line box.
  'display', 'flex', 'flex-direction', 'flex-wrap', 'flex-basis', 'flex-grow',
  'flex-shrink', 'justify-content', 'align-items', 'gap', 'vertical-align',
  // Paint — the "color scheme". Backgrounds were already stripped; borders,
  // shadows and transforms belong to the same class of borrowed chrome.
  'background', 'background-color', 'background-image', 'background-size',
  'background-position', 'box-shadow', 'transform', 'opacity',
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-width', 'border-style', 'border-color', 'border-top-color',
  'border-radius',
  // Wrapping — `nowrap` / `pre` on a long line is the other way content
  // escapes its container. The composer sets its own `pre-wrap`.
  'white-space',
  // Interaction/animation — never meaningful on pasted text, and `animation`
  // referencing a keyframe that exists in the app made pasted text move.
  'animation', 'animation-name', 'animation-duration', 'animation-timing-function',
  'animation-delay', 'animation-iteration-count', 'animation-fill-mode',
  'cursor', 'pointer-events', 'user-select', 'position',
];

/**
 * A class token styled-components generates for its own components.
 *
 * styled-components stamps TWO tokens on every element it renders: this
 * `sc-`-prefixed componentId, and a generated hash class that carries the
 * actual rules. They always appear together, and nothing the app authors by
 * hand is `sc-`-prefixed — so the componentId is a reliable marker for "this
 * element was rendered by a styled component", and finding it means the
 * whole class attribute is machinery rather than meaning.
 */
const STYLED_COMPONENTS_ID = /(^|\s)sc-[A-Za-z0-9]+(\s|$)/;

/**
 * True when an element's class list is styled-components machinery.
 *
 * Used by the RENDER path, which cannot simply drop every class: the app's own
 * chat HTML uses real ones (`chat-table-scroll`, `markdown-table`,
 * `interrupted-turn-notice`, `pending-widget-wrapper`, `language-*`). Dropping
 * the attribute only when an `sc-` token is present removes the styled
 * component's own hash class alongside it — which is the one that actually
 * paints — while leaving app-authored markup untouched.
 */
export function isStyledComponentsClassList(value: string | null | undefined): boolean {
  return STYLED_COMPONENTS_ID.test(String(value || ''));
}

// ─── Color parsing / WCAG contrast ───────────────────────────────────────────

/**
 * Named colors we can evaluate for contrast. Deliberately skewed toward the
 * light/pastel names that cause the "too white to read" bug; unknown names
 * are conservatively KEPT (we only replace when we can PROVE low contrast).
 */
const NAMED_COLORS: Record<string, [number, number, number]> = {
  white: [255, 255, 255],
  snow: [255, 250, 250],
  ivory: [255, 255, 240],
  floralwhite: [255, 250, 240],
  ghostwhite: [248, 248, 255],
  whitesmoke: [245, 245, 245],
  seashell: [255, 245, 238],
  oldlace: [253, 245, 230],
  linen: [250, 240, 230],
  lavenderblush: [255, 240, 245],
  mistyrose: [255, 228, 225],
  aliceblue: [240, 248, 255],
  azure: [240, 255, 255],
  mintcream: [245, 255, 250],
  honeydew: [240, 255, 240],
  cornsilk: [255, 248, 220],
  lemonchiffon: [255, 250, 205],
  lightyellow: [255, 255, 224],
  lightcyan: [224, 255, 255],
  beige: [245, 245, 220],
  gainsboro: [220, 220, 220],
  lightgray: [211, 211, 211],
  lightgrey: [211, 211, 211],
  silver: [192, 192, 192],
  darkgray: [169, 169, 169],
  darkgrey: [169, 169, 169],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  dimgray: [105, 105, 105],
  dimgrey: [105, 105, 105],
  black: [0, 0, 0],
  red: [255, 0, 0],
  green: [0, 128, 0],
  blue: [0, 0, 255],
  navy: [0, 0, 128],
  maroon: [128, 0, 0],
  purple: [128, 0, 128],
  teal: [0, 128, 128],
  olive: [128, 128, 0],
  orange: [255, 165, 0],
  yellow: [255, 255, 0],
  lime: [0, 255, 0],
  aqua: [0, 255, 255],
  cyan: [0, 255, 255],
  magenta: [255, 0, 255],
  fuchsia: [255, 0, 255],
  brown: [165, 42, 42],
};

/**
 * Parse a CSS color into [r, g, b] (0-255), alpha-composited over the
 * composer background when an alpha channel is present.
 * Returns null when the color cannot be evaluated (var(), currentcolor,
 * exotic spaces …) — callers must treat null as "keep the color".
 */
function parseCssColor(raw: string): [number, number, number] | null {
  const value = (raw || '').trim().toLowerCase();
  if (!value) return null;

  // #rgb / #rgba / #rrggbb / #rrggbbaa
  const hex = /^#([0-9a-f]{3,8})$/.exec(value);
  if (hex) {
    const h = hex[1];
    if (h.length === 3 || h.length === 4) {
      const r = parseInt(h[0] + h[0], 16);
      const g = parseInt(h[1] + h[1], 16);
      const b = parseInt(h[2] + h[2], 16);
      const a = h.length === 4 ? parseInt(h[3] + h[3], 16) / 255 : 1;
      return compositeOverBackground([r, g, b], a);
    }
    if (h.length === 6 || h.length === 8) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
      return compositeOverBackground([r, g, b], a);
    }
    return null;
  }

  // rgb() / rgba() — comma or space separated
  const rgb = /^rgba?\(\s*([\d.]+%?)\s*[, ]\s*([\d.]+%?)\s*[, ]\s*([\d.]+%?)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/.exec(value);
  if (rgb) {
    const channel = (c: string): number =>
      c.endsWith('%') ? (parseFloat(c) / 100) * 255 : parseFloat(c);
    const r = channel(rgb[1]);
    const g = channel(rgb[2]);
    const b = channel(rgb[3]);
    let a = 1;
    if (rgb[4] != null) {
      a = rgb[4].endsWith('%') ? parseFloat(rgb[4]) / 100 : parseFloat(rgb[4]);
    }
    if ([r, g, b, a].some((n) => Number.isNaN(n))) return null;
    return compositeOverBackground([r, g, b], a);
  }

  // hsl() / hsla()
  const hsl = /^hsla?\(\s*([\d.]+)(?:deg)?\s*[, ]\s*([\d.]+)%\s*[, ]\s*([\d.]+)%\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/.exec(value);
  if (hsl) {
    const h = ((parseFloat(hsl[1]) % 360) + 360) % 360;
    const s = parseFloat(hsl[2]) / 100;
    const l = parseFloat(hsl[3]) / 100;
    let a = 1;
    if (hsl[4] != null) {
      a = hsl[4].endsWith('%') ? parseFloat(hsl[4]) / 100 : parseFloat(hsl[4]);
    }
    if ([h, s, l, a].some((n) => Number.isNaN(n))) return null;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let rgb1: [number, number, number];
    if (h < 60) rgb1 = [c, x, 0];
    else if (h < 120) rgb1 = [x, c, 0];
    else if (h < 180) rgb1 = [0, c, x];
    else if (h < 240) rgb1 = [0, x, c];
    else if (h < 300) rgb1 = [x, 0, c];
    else rgb1 = [c, 0, x];
    return compositeOverBackground(
      [Math.round((rgb1[0] + m) * 255), Math.round((rgb1[1] + m) * 255), Math.round((rgb1[2] + m) * 255)],
      a
    );
  }

  if (value in NAMED_COLORS) return NAMED_COLORS[value];
  return null;
}

/** Composite a color with alpha over the (opaque) composer background. */
function compositeOverBackground(
  [r, g, b]: [number, number, number],
  alpha: number
): [number, number, number] {
  const a = Math.max(0, Math.min(1, alpha));
  if (a >= 1) return [r, g, b];
  const bg = parseHexStrict(COMPOSER_BACKGROUND_COLOR);
  return [
    a * r + (1 - a) * bg[0],
    a * g + (1 - a) * bg[1],
    a * b + (1 - a) * bg[2],
  ];
}

function parseHexStrict(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** WCAG relative luminance of an sRGB color (channels 0-255). */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two colors (order-independent). */
function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * True when `color` is too light to read on the composer background —
 * i.e. it should be replaced with the scheme text color.
 * Unparseable colors return false (kept — "if and only if too white").
 */
export function isColorTooLightForComposer(color: string): boolean {
  return isColorTooCloseTo(color, COMPOSER_BACKGROUND_COLOR);
}

/**
 * True when `color` is illegible against `background` (below the same 2.5:1
 * threshold the composer pass uses). Unparseable colors return false — we only
 * intervene when we can PROVE low contrast. Generalized 2026-08-27 so the
 * render-time bubble heal can test against the bubble's ACTUAL ground (gold
 * for the viewer's own messages, near-white for everyone else's).
 */
export function isColorTooCloseTo(color: string, background: string): boolean {
  const rgb = parseCssColor(color);
  const bg = parseCssColor(background);
  if (!rgb || !bg) return false;
  return contrastRatio(rgb, bg) < MIN_PASTE_CONTRAST_RATIO;
}

// ─── Font-size normalization ─────────────────────────────────────────────────

/** Parse a font-size value into em units; null when not confidently parseable. */
function fontSizeToEm(raw: string): number | null {
  const value = (raw || '').trim().toLowerCase();
  const m = /^([\d.]+)(px|pt|em|rem|%)$/.exec(value);
  if (!m) return null; // keywords (large, xx-large…) and exotic units: keep
  const n = parseFloat(m[1]);
  if (Number.isNaN(n)) return null;
  switch (m[2]) {
    case 'px': return n / 16;
    case 'pt': return n / 12; // 16px == 12pt
    case 'em': return n;
    case 'rem': return n;
    case '%': return n / 100;
    default: return null;
  }
}

// ─── Structure helpers ───────────────────────────────────────────────────────

const BLOCK_TAGS = new Set([
  'DIV', 'P', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'HR', 'TABLE', 'THEAD',
  'TBODY', 'TFOOT', 'TR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SECTION',
  'ARTICLE', 'HEADER', 'FOOTER', 'ASIDE', 'MAIN', 'NAV', 'FIGURE', 'FIGCAPTION',
]);

function isWhitespaceText(node: Node): boolean {
  return node.nodeType === Node.TEXT_NODE && !(node.textContent || '').trim();
}

function isBr(node: Node): boolean {
  return node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'BR';
}

/**
 * An element rendering as pure blank space: no visible text and no embedded
 * image. (<hr> is visible content — a rule — so it is never "empty".)
 */
function isEmptyBlock(node: Node): boolean {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const el = node as Element;
  if (el.tagName === 'BR' || el.tagName === 'HR' || el.tagName === 'IMG') return false;
  if (el.querySelector('img')) return false;
  return !(el.textContent || '').trim();
}

/** Trimmable at the edges: whitespace text, <br>, or an empty block. */
function isTrimmableEdge(node: Node): boolean {
  if (isWhitespaceText(node) || isBr(node)) return true;
  if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.TEXT_NODE) return true;
  return isEmptyBlock(node);
}

/**
 * Remove leading/trailing blank space from an HTML fragment: empty block
 * elements, <br> chains, and whitespace-only text nodes at either edge of the
 * top level. Middle content is untouched — safe to run on ANY composed
 * message (used by the send path), not just pastes.
 */
export function trimEdgeEmptyBlocks(html: string): string {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(
      `<div data-paste-root>${html}</div>`,
      'text/html'
    );
    const root = doc.querySelector('div[data-paste-root]');
    if (!root) return html;
    while (root.firstChild && isTrimmableEdge(root.firstChild)) {
      root.removeChild(root.firstChild);
    }
    while (root.lastChild && isTrimmableEdge(root.lastChild)) {
      root.removeChild(root.lastChild);
    }
    return root.innerHTML;
  } catch {
    return html.trim();
  }
}

// ─── Main entry points ───────────────────────────────────────────────────────

/**
 * Sanitize clipboard `text/html` for insertion into the composer.
 *
 * Pipeline:
 *   1. Drop scaffolding/dangerous elements ENTIRELY (script/style/iframe/…
 *      including their content).
 *   2. Reuse the composer's existing allowlist sanitizer (tags, attributes,
 *      style properties, javascript: URL stripping, anchor hardening).
 *   3. Contrast fix: inline `color`s below MIN_PASTE_CONTRAST_RATIO against
 *      the composer background are replaced with the scheme text color;
 *      adequate colors are KEPT. `background`/`background-color` are stripped
 *      outright; `font-size` above MAX_PASTE_FONT_SIZE_EM is capped.
 *   4. Structure trim: edge blank blocks/<br> chains removed, middle runs of
 *      consecutive empty blocks collapsed to one, inter-block newline text
 *      nodes dropped (they render as blank lines under `pre-wrap`).
 */
export function sanitizePastedHtml(html: string, opts?: { textColor?: string }): string {
  if (!html) return '';
  const textColor = opts?.textColor || COMPOSER_TEXT_COLOR;

  let cleaned: string;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // 1. Remove scaffolding + dangerous elements INCLUDING their content.
    //    (sanitizeHtmlInput would UNWRAP these, leaking style/script text.)
    doc.querySelectorAll(
      'script, style, iframe, object, embed, noscript, meta, link, title, base, template, svg, math, form, input, textarea, select, option'
    ).forEach((el) => el.remove());
    // 1b. Elements the source page HID are deleted, not revealed (2026-08-27
    //     review fix). This MUST happen on the RAW parse, before the
    //     allowlist pass: `visibility` is not an allowed property (stripped
    //     with the element kept) and a bare skip-link anchor is unwrapped to
    //     its text — either way the hidden marker is gone downstream and the
    //     invisible text would leak into the paste.
    if (doc.body) removeHiddenElements(doc.body);
    cleaned = doc.body ? doc.body.innerHTML : '';
  } catch {
    // No DOM available — degrade to tag-stripped plain text.
    return sanitizePastedText(html.replace(/<\/?[^>]+(>|$)/g, ''));
  }

  // 2. Existing allowlist sanitizer (safety: tags/attrs/styles/URLs).
  const safe = sanitizeHtmlInput(cleaned);

  try {
    const doc = new DOMParser().parseFromString(
      `<div data-paste-root>${safe}</div>`,
      'text/html'
    );
    const root = doc.querySelector('div[data-paste-root]');
    if (!root) return safe;

    // 3a. CLASSES GO (owner report 2026-08-26). Every one of them, on every
    //     element, unconditionally.
    //
    //     This is the larger half of the reported bug. `sanitizeHtmlInput`
    //     keeps `class` on every allowed tag; a browser copy of the transcript
    //     serialises the styled-components classes of the message bubble and
    //     its row; and those classes still resolve against the app's own
    //     injected stylesheet once the markup is back inside the app. So the
    //     pasted fragment re-acquired the gold bubble background, the white
    //     text, the padding, the border radius and the 80%-of-parent width —
    //     nested inside a bubble that already had all of them. Stripping the
    //     inline `background` (below) could never help: the gold comes from a
    //     class rule marked `!important`.
    //
    //     Nothing pasted needs a class. The app re-applies its own where they
    //     matter (wrapTablesForScroll re-wraps tables at render), so this is a
    //     clean removal rather than a trade.
    root.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));

    // 3b. Style hygiene on every element carrying inline styles.
    root.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
      const style = el.style;

      // Geometry, spacing, layout and paint go; typography stays. See
      // DROPPED_PASTE_STYLE_PROPERTIES for the reasoning and the owner's line.
      for (const property of DROPPED_PASTE_STYLE_PROPERTIES) {
        style.removeProperty(property);
      }

      // Contrast fix — replace IF AND ONLY IF the color is too light to read.
      const color = style.getPropertyValue('color');
      if (color && isColorTooLightForComposer(color)) {
        style.setProperty('color', textColor);
      }

      // Cap enormous font sizes (> MAX_PASTE_FONT_SIZE_EM em); keep the rest.
      const fontSize = style.getPropertyValue('font-size');
      if (fontSize) {
        const em = fontSizeToEm(fontSize);
        if (em != null && em > MAX_PASTE_FONT_SIZE_EM) {
          style.setProperty('font-size', `${MAX_PASTE_FONT_SIZE_EM}em`);
        }
      }

      if (!style.length) el.removeAttribute('style');
    });

    // 4a. Edge trim: leading/trailing empty blocks, <br> chains, whitespace.
    while (root.firstChild && isTrimmableEdge(root.firstChild)) {
      root.removeChild(root.firstChild);
    }
    while (root.lastChild && isTrimmableEdge(root.lastChild)) {
      root.removeChild(root.lastChild);
    }

    // 4b. Inter-block newline text nodes render as blank lines under the
    //     composer's `white-space: pre-wrap` — drop whitespace-only text
    //     nodes that contain a newline and sit beside a block element.
    //     (A plain single space between inline elements has no newline and
    //     is kept, so inline spacing is untouched.)
    const dropInterBlockWhitespace = (parent: Element): void => {
      Array.from(parent.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          dropInterBlockWhitespace(child as Element);
          return;
        }
        if (!isWhitespaceText(child) || !/[\r\n]/.test(child.textContent || '')) return;
        const prev = child.previousSibling;
        const next = child.nextSibling;
        const besideBlock = (n: Node | null): boolean =>
          !!n && n.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((n as Element).tagName);
        if (besideBlock(prev) || besideBlock(next)) {
          parent.removeChild(child);
        }
      });
    };
    dropInterBlockWhitespace(root);

    // 4c. Collapse middle runs of >1 consecutive empty blocks to one.
    //     (One empty block == one intentional blank line; runs are the
    //     un-deletable padding users complained about.)
    let inEmptyRun = false;
    Array.from(root.childNodes).forEach((child) => {
      if (isEmptyBlock(child)) {
        if (inEmptyRun) {
          root.removeChild(child);
        } else {
          inEmptyRun = true;
        }
      } else if (isWhitespaceText(child)) {
        // whitespace between empty blocks does not break the run
      } else {
        inEmptyRun = false;
      }
    });

    return root.innerHTML;
  } catch {
    return safe;
  }
}

/**
 * Sanitize clipboard plain text: trim leading/trailing blank lines (and the
 * trailing line's dangling whitespace) while keeping ALL internal structure —
 * internal blank lines, indentation, etc. are the user's formatting.
 */
export function sanitizePastedText(text: string): string {
  if (!text) return '';
  return text
    .replace(/^(?:[^\S\r\n]*\r?\n)+/, '') // leading blank lines
    .replace(/(?:\r?\n[^\S\r\n]*)+$/, '') // trailing blank lines
    .replace(/[^\S\r\n]+$/, '');          // dangling spaces/tabs on the last line
}

/**
 * Convert sanitized plain text to composer HTML: escape markup so pasted
 * text can never be parsed as HTML, and preserve line structure as <br>.
 * (The composer state is HTML — see the paste handler in useGlobalChat.)
 */
export function plainTextToComposerHtml(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\r\n|\r|\n/g, '<br>');
}

/**
 * Elements the SOURCE page hid must stay hidden — by not existing here at all.
 *
 * 2026-08-27 review fix: the paste pipeline drops `display`/`opacity` (they
 * are box/paint properties), which un-hid content the source page kept
 * invisible — pasted pages' skip-links, template junk, screen-reader-only
 * text. Deleting the node outright honours the source's intent and is the
 * only order that works: the check must run BEFORE the property is stripped.
 */
export function removeHiddenElements(root: Element): void {
  root.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    const style = el.style;
    const display = style.getPropertyValue('display').trim().toLowerCase();
    const visibility = style.getPropertyValue('visibility').trim().toLowerCase();
    const opacity = parseFloat(style.getPropertyValue('opacity'));
    if (
      display === 'none'
      || visibility === 'hidden'
      || visibility === 'collapse'
      || (Number.isFinite(opacity) && opacity === 0)
    ) {
      el.remove();
    }
  });
}

/**
 * RENDER-TIME heal for STORED user-message html (owner report 2026-08-27:
 * Justin's /activity view showed a user message as an enormous empty gold
 * bubble with its text pushed off-screen).
 *
 * The paste sanitizer protects NEW sends; messages stored BEFORE it shipped —
 * or delivered from another member's pre-fix client — still carry pasted
 * styled-components class lists (which re-resolve against the app's own live
 * stylesheet and repaint a full bubble INSIDE the bubble), inline geometry
 * (width/min-width defeats the CSS max-width backstop outright — min-width
 * beats max-width), nowrap runs whose ink overflows any box cap, and inline
 * colors copied from a differently-coloured ground (white text, invisible on
 * the light bubble).
 *
 * Stored data is deliberately NOT rewritten — the viewer cannot even write
 * other members' stored messages — so the heal runs at render, on every
 * paint, and is IDEMPOTENT: post-fix composer html carries no classes and no
 * box styles, so on healthy messages this is a no-op.
 *
 * USER bubbles only. Assistant html legitimately uses classes
 * (chat-table-scroll, widget wrappers) and box styles; user html has exactly
 * one producer — the composer — and needs neither.
 */
export function healUserBubbleHtml(html: string, opts?: { background?: string }): string {
  if (!html) return '';
  // Fast path: nothing a heal acts on — skip the DOM round-trip entirely.
  if (!/class=|style=/.test(html)) return html;
  const background = opts?.background || COMPOSER_BACKGROUND_COLOR;
  try {
    const doc = new DOMParser().parseFromString(
      `<div data-heal-root>${html}</div>`,
      'text/html',
    );
    const root = doc.querySelector('div[data-heal-root]');
    if (!root) return html;

    // Content the source hid stays gone — before its hiding property is stripped.
    removeHiddenElements(root);

    // Styled-components machinery re-resolves against the live stylesheet —
    // the whole class list goes when the sc- marker is present. (Conservative:
    // an app-authored class in user html does not exist today, but a plain
    // class list is left alone all the same.)
    root.querySelectorAll('[class]').forEach((el) => {
      if (isStyledComponentsClassList(el.getAttribute('class'))) el.removeAttribute('class');
    });

    // Box/geometry/paint styles go; typography stays — the same line the
    // paste sanitizer draws, applied to what was stored before it existed.
    root.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
      const style = el.style;
      for (const property of DROPPED_PASTE_STYLE_PROPERTIES) {
        style.removeProperty(property);
      }
      // An inline color copied from a different ground can be invisible on
      // THIS one. Strip (not rewrite) so the bubble's own default cascades.
      const color = style.getPropertyValue('color');
      if (color && isColorTooCloseTo(color, background)) {
        style.removeProperty('color');
      }
      if (!style.length) el.removeAttribute('style');
    });

    return root.innerHTML;
  } catch {
    return html; // no DOM / unparseable — the stored markup stands as before
  }
}
